import React, { useEffect, useMemo, useRef } from 'react';
import { ArrowLeft, MoreVertical, Trash2, Lock, Loader2, Info, Smartphone, Users, RefreshCw, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuCheckboxItem, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import MessageBubble from './MessageBubble';
import ReplyBubble from './ReplyBubble';
import SeenNotice from './SeenNotice';
import ReplyComposer from './ReplyComposer';
import SuggestionChips from './SuggestionChips';
import { transcriptRows, blockedReasonText, BLOCKED_REASON_WORDS, fmtWhen } from '../../lib/chatsView';
import { retentionPhrase } from '../../lib/chatDataView';
import { useChatPics } from '../../lib/useChatPics';

/** r16q5: the chat's picture in the header; the group mark or the initial until the worker has one. */
function HeaderPic({ thread }) {
  const picOf = useChatPics([thread]);
  const pic = picOf(thread);
  if (pic) return <img src={pic} alt="" className="h-full w-full rounded-full object-cover" data-testid="thread-header-pic" />;
  return thread.isGroup ? <Users className="h-4 w-4" /> : thread.peer.initial;
}

/*
 * r16p (design 7.1): the open conversation - header with the thread menu ("Don't open on the phone", "Delete conversation"), the
 * "Seen" notice, the slot's PREVIEW / TikTok lines, the transcript (older pages on demand: a whole thread is never loaded at once),
 * our queued replies with their states and the HELD cards, AI suggestions and the composer.
 */

/** Why the composer is closed, in words (null = open). The server's own reason wins (replies_allowed=false + its code). */
export function composerClosedReason({ thread, slot, info }) {
  if (!thread) return 'Pick a conversation.';
  const platform = (slot && slot.platform) || thread.platform;
  if (platform === 'TIKTOK') return (info && info.tiktokNote) || BLOCKED_REASON_WORDS.platform_not_supported;
  if (slot && slot.mode === 'PREVIEW') return BLOCKED_REASON_WORDS.preview_only;
  if (thread.detached) return BLOCKED_REASON_WORDS.conversation_detached;
  if (thread.repliesAllowed === false) return blockedReasonText(thread.repliesBlocked, thread.repliesBlockedMessage);
  if (slot && slot.replyMode === 0) return BLOCKED_REASON_WORDS.slot_reply_mode_off;
  if (slot && slot.syncEnabled === false) return BLOCKED_REASON_WORDS.slot_chat_sync_off;
  return null;
}

export default function ThreadView({
  detail, loading, error, info, now, draft, onDraft, onSend, sending, onCancel, onRelease, onRequeue, replyBusy, older, onLoadOlder,
  olderLoading, olderCursor, onBack, onToggleNeverOpen, onDelete, userId, onComposerHeight, onPickSuggestion, onRetry, messageActions = null, replyTo = null, onReplyToClear = null, sendNotice = null, composerHint = null, liveNow = false, onOlderFromPhone = null,
}) {
  const scrollRef = useRef(null);
  // r16q12: scrolled to the top of what's here: what's stored comes next. r16q19 (owner 11-05: "it's not stopping, even after switching the chat"): the phone goes further back only on the
  // button. A scroll event isn't a person asking: new rows move the scroll, and a newly opened chat starts at the top, so this kept pressing it by itself
  const lastOlderAt = useRef(0);
  const onTranscriptScroll = (e) => {
    if (e.currentTarget.scrollTop > 40) return;
    const t = Date.now();
    if (t - lastOlderAt.current < 2500) return;
    lastOlderAt.current = t;
    if (olderCursor && !olderLoading && onLoadOlder) onLoadOlder();
  };
  const bottomRef = useRef(null);
  const thread = detail && detail.thread;
  const slot = detail && detail.slot;
  const messages = useMemo(() => [...(older || []), ...((detail && detail.messages) || [])], [older, detail]);
  const rows = useMemo(() => transcriptRows(messages, now), [messages, now]);
  const replies = useMemo(() => {
    const shown = new Set(messages.map((m) => m.replyId).filter(Boolean));
    return ((detail && detail.replies) || []).filter((r) => !(r.status === 'sent' && shown.has(r.id)));
  }, [detail, messages]);
  const lastKey = messages.length ? messages[messages.length - 1].id : '';
  const replyKey = replies.map((r) => `${r.id}:${r.status}`).join(',');

  // keep the newest message in view when the thread changes or a new message / reply state arrives (not when older pages load)
  useEffect(() => {
    if (bottomRef.current && bottomRef.current.scrollIntoView) bottomRef.current.scrollIntoView({ block: 'end' });
  }, [thread && thread.id, lastKey, replyKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-6 text-center" data-testid="thread-error">
        <p className="max-w-sm text-sm text-muted-foreground text-pretty">{error}</p>
        <div className="flex gap-2">
          {onBack && <Button variant="outline" size="sm" className="tap rounded-full sm:hidden" onClick={onBack}><ArrowLeft className="h-4 w-4" />Chats</Button>}
          {onRetry && <Button variant="outline" size="sm" className="tap rounded-full" onClick={onRetry}><RefreshCw className="h-4 w-4" />Try again</Button>}
        </div>
      </div>
    );
  }
  if (loading || !thread) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-3 p-4" aria-busy="true" aria-label="Loading the conversation" data-testid="thread-loading">
        <div className="h-10 w-1/2 animate-pulse rounded-xl bg-secondary/60" />
        <div className="h-14 w-3/5 animate-pulse rounded-2xl bg-secondary/60" />
        <div className="ml-auto h-10 w-2/5 animate-pulse rounded-2xl bg-secondary/60" />
        <div className="h-16 w-4/5 animate-pulse rounded-2xl bg-secondary/60" />
      </div>
    );
  }

  const closed = composerClosedReason({ thread, slot, info });
  const aiOn = !!(info && info.ai && info.ai.enabled) && !!slot && slot.replyMode === 2 && !closed;
  const platform = (slot && slot.platform) || thread.platform;

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="thread-view" data-thread={thread.id}>
      {/* header */}
      <div className="flex items-center gap-2 border-b border-border/70 px-3 py-2.5 sm:px-4">
        {onBack && (
          <Button variant="ghost" size="sm" className="tap -ml-1 shrink-0 rounded-full px-2 sm:hidden" onClick={onBack} data-testid="thread-back" aria-label="Back to the conversations">
            <ArrowLeft className="h-4 w-4" />Chats
          </Button>
        )}
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary/25 to-accent/25 text-sm font-semibold ring-1 ring-white/10" aria-hidden>
          <HeaderPic thread={thread} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-heading text-[15px] font-semibold leading-tight" data-testid="thread-title">{thread.peer.display}</p>
          <p className="truncate text-xs text-muted-foreground">
            {thread.peer.username && thread.peer.display !== `@${thread.peer.username}` ? <span className="font-mono">@{thread.peer.username}</span> : null}
            {thread.peer.username && thread.slot ? ' · ' : ''}
            {thread.slot ? <span className="font-mono">{thread.slot}</span> : null}
            {thread.folder && thread.folder !== 'primary' ? <span className="capitalize"> · {thread.folder}</span> : null}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="tap shrink-0 text-muted-foreground hover:text-foreground" aria-label="Conversation menu" data-testid="thread-menu">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuCheckboxItem checked={!!thread.neverOpen} onCheckedChange={(v) => onToggleNeverOpen(thread, v)} data-testid="thread-never-open">
              <Lock className="mr-2 h-4 w-4" />Don&apos;t open on the phone
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onDelete(thread)} className="text-danger focus:text-danger" data-testid="thread-delete">
              <Trash2 className="mr-2 h-4 w-4" />Delete conversation
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* transcript */}
      <div ref={scrollRef} onScroll={onTranscriptScroll} className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-3 py-3 sm:px-4" data-testid="thread-transcript">
        <div className="space-y-1.5">
          <SeenNotice thread={thread} now={now} />
          {platform === 'TIKTOK' && (
            <p className="flex items-start gap-1.5 text-xs text-muted-foreground" data-testid="thread-tiktok-line">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />{(info && info.tiktokNote) || BLOCKED_REASON_WORDS.platform_not_supported}
            </p>
          )}
          {slot && slot.mode === 'PREVIEW' && platform !== 'TIKTOK' && (
            <p className="flex items-start gap-1.5 text-xs text-muted-foreground" data-testid="thread-preview-line">
              <Smartphone className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />{BLOCKED_REASON_WORDS.preview_only}
            </p>
          )}
          {thread.neverOpen && (
            <p className="flex items-start gap-1.5 text-xs text-muted-foreground" data-testid="thread-never-open-line">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />The worker does not open this conversation on the phone; only its inbox preview is read.
            </p>
          )}
        </div>
        {olderCursor && (
          <div className="flex justify-center">
            <Button variant="outline" size="sm" className="tap h-8 rounded-full text-xs" onClick={onLoadOlder} disabled={olderLoading} data-testid="thread-older">
              {olderLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}Load older messages
            </Button>
          </div>
        )}
        {!olderCursor && liveNow && onOlderFromPhone && (
          <div className="flex justify-center">
            <Button variant="outline" size="sm" className="tap h-8 rounded-full text-xs" onClick={onOlderFromPhone} data-testid="thread-older-phone">Load older from the phone</Button>
          </div>
        )}
        {rows.length === 0 && replies.length === 0 && (
          <div className="mx-auto max-w-sm rounded-xl border border-dashed border-border/80 p-4 text-center text-sm text-muted-foreground text-pretty" data-testid="thread-empty">
            {slot && slot.mode === 'PREVIEW'
              ? "Only the inbox preview of this conversation is read on this phone's Instagram version."
              : `Only the inbox preview of this conversation has been read so far. Its messages appear here once the worker opens it on the phone (to send a reply)${info && info.retentionDays ? `, and are kept ${retentionPhrase(info.retentionDays)}` : ''}.`}
            {thread.preview && thread.preview.text ? (
              <span className="mt-2 block rounded-lg bg-secondary/50 px-3 py-2 text-left text-foreground/90 whitespace-pre-wrap break-words" data-testid="thread-empty-preview">{thread.preview.text}</span>
            ) : null}
          </div>
        )}
        {rows.map((row) => {
          if (row.type === 'date') {
            return (
              <div key={row.key} className="flex justify-center py-1" data-testid="date-row">
                <span className="rounded-full bg-secondary/60 px-2.5 py-0.5 text-[11px] text-muted-foreground">{row.label}</span>
              </div>
            );
          }
          if (row.type === 'gap') {
            return <p key={row.key} className="text-center text-[11px] text-muted-foreground" data-testid="gap-row">Earlier messages were not read</p>;
          }
          return <MessageBubble key={row.key} m={row.m} actions={liveNow ? messageActions : null} />;
        })}
        {replies.map((r) => (
          <ReplyBubble key={r.id} liveNow={liveNow} reply={r} peerUsername={thread.peer.username} onCancel={onCancel} onRelease={onRelease} onRequeue={onRequeue} busy={replyBusy === r.id} now={now} />
        ))}
        {liveNow ? (
          <p className="pt-1 text-center text-[11px] text-success" data-testid="thread-live-line">Live: this chat is read from the phone every few seconds, while the phone is on it.</p>
        ) : slot && slot.lastSyncAt && (
          <p className="pt-1 text-center text-[11px] text-muted-foreground" data-testid="thread-synced">
            Inbox last read {fmtWhen(slot.lastSyncAt, now)}. New messages arrive at the slot&apos;s sessions, not in real time.
          </p>
        )}
        <div ref={bottomRef} />
      </div>

      {aiOn && (
        <SuggestionChips key={thread.id} threadId={thread.id} ai={info.ai} userId={userId} disabled={sending}
          onPick={(text, suggestionId) => onPickSuggestion(thread.id, text, suggestionId)} />
      )}
      {sendNotice && !closed && (
        // review P2: the last send of THIS text got no answer; the next Send repeats that same request (it cannot queue twice)
        <p className="mx-3 mt-2 flex items-start gap-2 rounded-xl border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-foreground" role="status" data-testid="send-unanswered">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />{sendNotice}
        </p>
      )}
      {replyTo && !closed && (
        <div className="mx-3 mt-2 flex items-start gap-2 rounded-xl border border-border/70 bg-secondary/40 px-3 py-2" data-testid="reply-to-bar">
          <div className="min-w-0 flex-1 text-xs">
            <p className="font-medium text-foreground">Replying to {replyTo.dir === 'out' ? 'your message' : 'their message'}</p>
            <p className="line-clamp-2 break-words text-muted-foreground" data-testid="reply-to-text">{typeof replyTo.text === 'string' && replyTo.text !== '' ? replyTo.text : 'A message'}</p>
          </div>
          <button type="button" className="tap shrink-0 rounded px-1.5 text-xs text-muted-foreground hover:text-foreground" onClick={onReplyToClear} aria-label="Not replying to this message" data-testid="reply-to-clear">Cancel</button>
        </div>
      )}
      <ReplyComposer
        key={`c-${thread.id}`}
        value={draft}
        onChange={onDraft}
        onSend={() => onSend(thread.id)}
        disabled={!!closed}
        disabledReason={closed}
        busy={sending}
        maxLength={(info && info.maxReplyLength) || 1000}
        newlines={!!(info && info.newlines)}
        onHeight={onComposerHeight}
        hint={composerHint}
      />
    </div>
  );
}

export function NoThreadPicked({ className }) {
  return (
    <div className={cn('flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-8 text-center', className)} data-testid="thread-none">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20" aria-hidden>
        <Info className="h-5 w-5" />
      </span>
      <p className="text-sm font-medium">Pick a conversation</p>
      <p className="max-w-xs text-xs text-muted-foreground text-pretty">
        Replies go out from the phone at the slot&apos;s next session, at human pace. Nothing here is real time.
      </p>
    </div>
  );
}
